import React, { Dispatch, SetStateAction, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { CategoryDA } from '../../services/admin/category.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { Category } from '../../models/category.model';
import { GlobalSwal } from '../../styles/swal.style';
import { AccountingAccount } from '../../models/accounting.account.model';
import { legendInvalid, legendIconInvalid, legendValidInvalidRestart, legendIconValidInvalidRestart, legendValid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { isValidObjectAttributes } from '../../scripts/compare.object.script';
import { suffixLegend } from '../../scripts/name.legend.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppCategoryForm from '../../interfaces/main/inventory/category/component/CategoryForm';
import AppLoading from '../Loading';

export interface AppModalCategoryCreateProps {
  companyForUser: Company | null,
  categoriesForCompany: Category[] | null,
  accountingExpenseAccountsForCompany: AccountingAccount[] | null,
  accountingVatAccountsForCompany: AccountingAccount[] | null,
  accountingIncomeAccountsForCompany: AccountingAccount[] | null,
  category?: {value: string, valid: boolean, object: any},
  categories?: Category[] | null,
  setCategoriesForCompany: Dispatch<SetStateAction<Category[] | null>>,
  setCategory?: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setCategories?: Dispatch<SetStateAction<Category[] | null>>,
  setInventoryAccounts?: Dispatch<SetStateAction<{value: {expense: string, vat: string, income: string}, valid: {expense: boolean, vat: boolean, income: boolean}}>>
};

let errorResponse: Error, categoryResponse: Category;

const AppModalCategoryCreate: React.FunctionComponent<AppModalCategoryCreateProps> = ({companyForUser, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, category, categories, setCategoriesForCompany, setCategory, setCategories, setInventoryAccounts}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [description, setDescription] = useState({value: '', valid: false})
  const [names, setNames] = useState({value: {en: '', fr: '', it: '', de: ''}, valid: {en: true, fr: true, it: true, de: true}})
  const [accounts, setAccounts] = useState({value: {expense: '', vat: '', income: ''}, valid: {expense: false, vat: false, income: false}})

  const submitCategoryCreate = async () => {
    setLoadIndicator('on')

    let namesIsValid: boolean = isValidObjectAttributes(names.valid)
    let accountsIsValid: boolean = isValidObjectAttributes(accounts.valid)

    if (companyForUser && categoriesForCompany && name.valid && description.valid && namesIsValid && accountsIsValid) {
      await CategoryDA.postCategory(companyForUser.id, name.value, description.value, names.value, accounts.value,).then( (response) => {
        if (response.status === 201) {
          categoryResponse = response.data
          setCategoriesForCompany([categoryResponse, ...categoriesForCompany])

          if (category && setCategory) {
            setCategory({value: categoryResponse.id, valid: true, object: categoryResponse})
            legendValid('legend-category-required')
          }

          if (categories && setCategories) {
            setCategories([categoryResponse, ...categories])
          }

          if (setInventoryAccounts) {
            setInventoryAccounts({value: {expense: categoryResponse.accounts.expense, vat: categoryResponse.accounts.vat, income: categoryResponse.accounts.income}, valid: {expense: true, vat: true, income: true}})
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedCategory,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalCategoryCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: `${errorResponse.message}!`,
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value.length) legendIconInvalid('modal-category-create-input-name', 'modal-category-create-legend-name-required')
        if (!description.value.length) legendInvalid('modal-category-create-legend-description-required')
        if (!accounts.value.expense.length) legendInvalid('modal-category-create-legend-account-expense-required')
        if (!accounts.value.vat.length) legendInvalid('modal-category-create-legend-account-vat-required')
        if (!accounts.value.income.length) legendInvalid('modal-category-create-legend-account-income-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalCategoryCreate = () => {
    modalHide('modal-category-create')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-category-create-input-name', 'modal-category-create-legend-name-valid')
    legendIconValidInvalidRestart('modal-category-create-input-name', 'modal-category-create-legend-name-required')
    legendValidInvalidRestart('modal-category-create-legend-description-valid')
    legendValidInvalidRestart('modal-category-create-legend-description-required')
    legendValidInvalidRestart('modal-category-create-legend-account-expense-required')
    legendValidInvalidRestart('modal-category-create-legend-account-vat-required')
    legendValidInvalidRestart('modal-category-create-legend-account-income-required')

    for (var index in Object.keys(names.valid)) {
      legendIconValidInvalidRestart(`modal-category-create-input-language-name${suffixLegend(index)}`, `modal-category-create-legend-language-name-valid${suffixLegend(index)}`)
    }

    if (category && setCategories) {
      legendValid('legend-category-required')
    }

    if (setInventoryAccounts) {
      legendValid('legend-account-expense-required')
      legendValid('legend-account-vat-required')
      legendValid('legend-account-income-required')
    }

    setName({value: '', valid: false})
    setDescription({value: '', valid: false})
    setNames({value: {en: '', fr: '', it: '', de: ''}, valid: {en: true, fr: true, it: true, de: true}})
    setAccounts({value: {expense: '', vat: '', income: ''}, valid: {expense: false, vat: false, income: false}})
  }

  return (
    <div id="modal-category-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.createCategory}</h2>
          </div>
          <div className="modal-body py-0">
            { companyForUser && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany
              ?
              <AppCategoryForm component={"modal-category-create"} name={name} description={description} names={names} accounts={accounts} setName={setName} setDescription={setDescription} setNames={setNames} setAccounts={setAccounts}></AppCategoryForm>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalCategoryCreate}>{lang.labels.cancel}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitCategoryCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalCategoryCreate;
